import { useEffect, useMemo, useState } from 'react'
import { useCampaignForm } from '../../../CampaignForm/hooks/useCampaignForm'
import { ScreenGroupModalProps } from '../types'
import {
  hasNoChange,
  isDuplicate,
  hasSameTags,
  compareTags,
  uniqueById
} from '../../helpers'

export function useModal({
  closeModal,
  isEditing,
  groupToEdit
}: ScreenGroupModalProps) {
  const { setScreenGroups } = useCampaignForm()
  const [selectedTags, setSelectedTags] = useState<string[] | null>(null)
  const [selectedScreens, setSelectedScreens] = useState<
    ListScreensByTagsResponse[] | null
  >(null)

  const hasSelectedTags = useMemo(
    () => selectedTags?.length > 0,
    [selectedTags]
  )

  const hasSelectedScreens = useMemo(
    () => selectedScreens?.length > 0,
    [selectedScreens]
  )

  const canConfirm = useMemo(
    () => hasSelectedTags && hasSelectedScreens,
    [hasSelectedTags, hasSelectedScreens]
  )

  function handleClose() {
    setSelectedTags(null)
    setSelectedScreens(null)
    closeModal()
  }

  function editGroup() {
    setScreenGroups(prevState => {
      // If there is no prevState or there are no changes, return the previous state
      if (
        !prevState ||
        hasNoChange({
          selectedTags,
          selectedScreens,
          group: groupToEdit?.group
        })
      )
        return prevState

      const newState = [...prevState]

      newState[groupToEdit.index] = {
        tags: selectedTags,
        screens: selectedScreens
      }

      // If it's a duplicate with another group, remove the group being edited
      if (
        isDuplicate({ selectedTags, selectedScreens, screenGroups: prevState })
      ) {
        newState.splice(groupToEdit.index, 1)
        return newState
      }

      // If there's another group with the same tags, merge the screens and remove the group being edited
      if (hasSameTags({ selectedTags, screenGroups: prevState })) {
        const groupIndex = newState.findIndex((group, index) => {
          return (
            index !== groupToEdit.index && compareTags({ selectedTags, group })
          )
        })

        if (groupIndex !== -1) {
          const updatedGroup = {
            ...newState[groupIndex],
            screens: uniqueById([
              ...newState[groupIndex].screens,
              ...selectedScreens!
            ])
          }
          newState[groupIndex] = updatedGroup
          newState.splice(groupToEdit.index, 1)
        }
      }

      // If it's not a duplicate and no same tags, just update the group being edited
      return newState
    })
  }

  function addGroup() {
    const newGroup: ScreenGroup = {
      tags: selectedTags,
      screens: selectedScreens
    }

    setScreenGroups(prevState => {
      // If there's no previous state, add the new group
      if (!prevState) return [newGroup]

      // If it's a duplicate, do not add it
      if (
        isDuplicate({ selectedTags, selectedScreens, screenGroups: prevState })
      ) {
        return prevState
      }

      // If it's not a duplicate and no same tags, add as new group
      if (!hasSameTags({ selectedTags, screenGroups: prevState })) {
        return [...prevState, newGroup]
      }

      // If there's a group with the same tags, merge the screens
      const groupIndex = prevState?.findIndex(group =>
        compareTags({ selectedTags, group })
      )
      if (typeof groupIndex === 'number' && groupIndex !== -1) {
        const updatedGroup = {
          ...prevState[groupIndex],
          screens: uniqueById([
            ...prevState[groupIndex]?.screens,
            ...selectedScreens!
          ])
        }
        const newState = [...prevState]
        newState[groupIndex] = updatedGroup

        return newState
      }
    })
  }

  function handleConfirm() {
    if (!canConfirm) return

    if (isEditing && !!groupToEdit) {
      editGroup()
      handleClose()
      return
    }

    addGroup()
    handleClose()
  }

  useEffect(() => {
    if (isEditing && !!groupToEdit) {
      setSelectedTags(groupToEdit?.group?.tags ?? null)
      setSelectedScreens(groupToEdit?.group?.screens ?? null)
    }
  }, [isEditing, groupToEdit])

  return {
    handleClose,
    canConfirm,
    handleConfirm,
    selectedTags,
    setSelectedTags,
    selectedScreens,
    setSelectedScreens
  }
}
